import {useCallback} from 'react';
import {generatePath} from '@legit.health/ui';
import {BodySiteId, ConditionId, renderQuestion} from '@legit.health/ui';
import {useNavigate} from '@tanstack/react-router';
import {useTranslation} from 'react-i18next';
import {useCurrentPatient} from '@/patient/context/CurrentPatientContext';
import {useAnalyzableConditions} from '@/patient/hooks/useConditions';
import {
  DIAGNOSTIC_REPORT_SENT,
  DIAGNOSTIC_REPORTS,
  GLOBAL_DIAGNOSTIC_REPORTS
} from '@/patient/routing/paths';
import {CreateDiagnosticReportResponse} from '@/shared/components/diagnosticReport/DiagnosticReportForm';
import DiagnosticReportForm from '@/shared/components/diagnosticReport/DiagnosticReportForm';
import {useCompany} from '@/shared/contexts/UserContext/useUserContext';
import {Encounter} from '@/shared/interfaces/Encounter';
import NoScoringSystems from './NoScoringSystem';

export default function DiagnosticReportFormModal({
  encounter,
  conditionId,
  bodySiteId,
  onSuccess
}: {
  encounter?: Encounter | null;
  conditionId?: ConditionId | null;
  bodySiteId?: BodySiteId | null;
  onSuccess?: () => void;
}) {
  const company = useCompany();
  const patient = useCurrentPatient();
  const navigate = useNavigate();
  const {t} = useTranslation(['ui']);
  const conditions = useAnalyzableConditions();
  const isFullBodySite =
    company.clinicalTrial &&
    company.clinicalTrial.definition?.pathologies?.length > 0 &&
    company.clinicalTrial.definition.pathologies.some((p) => p.bodySite === '');

  const handleSuccess = useCallback(
    function (response: CreateDiagnosticReportResponse) {
      if (company.isForClinicalTrials && isFullBodySite) {
        onSuccess?.();
        navigate({
          to: generatePath(GLOBAL_DIAGNOSTIC_REPORTS)
        });
        return;
      }
      if (patient.canViewResultsOfDiagnosticReports) {
        navigate({
          to: generatePath(
            DIAGNOSTIC_REPORTS,
            {},
            new URLSearchParams({tab: response.conditionId ? '0' : '2'})
          )
        });
      } else {
        navigate({to: generatePath(DIAGNOSTIC_REPORT_SENT)});
      }
      onSuccess?.();
    },
    [
      navigate,
      onSuccess,
      patient.canViewResultsOfDiagnosticReports,
      company.isForClinicalTrials,
      isFullBodySite
    ]
  );

  function extraPostData(conditionId: string | null): Record<string, any> {
    if (!encounter) {
      return {};
    }
    const conditionIdOfEncounter = encounter.conditionId ?? null;
    if (`${conditionId}` === `${conditionIdOfEncounter}`) {
      return {
        encounter: encounter.id
      };
    }
    return {};
  }

  return (
    <DiagnosticReportForm
      patient={patient}
      onSuccess={handleSuccess}
      extraPostData={extraPostData}
      conditionId={conditionId}
      bodySiteId={bodySiteId}
      conditions={conditions}
      noScoringSystems={<NoScoringSystems />}
      renderItemLabel={function ({item, scoringSystem, bodySite}) {
        const bodySiteGroup = scoringSystem.bodySiteGroups?.[bodySite.code];
        return renderQuestion({
          bodySiteName: bodySite.name,
          bodySiteGroup: bodySiteGroup ? t(`ui:bodySiteGroups.${bodySiteGroup}`) : undefined,
          question: item.question,
          name: item.name
        });
      }}
    />
  );
}
